import React from "react";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import type { ApolloClient } from "@apollo/client";
import { Col, Row } from "react-bootstrap";
import { push } from "store/actions/router";
import { FiGlobe } from "react-icons/fi";
import { toast } from "react-toastify";
import { formatCurrency } from "utils/number";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import type { RootState } from "types/state";

import moment from "moment";
import Button from "components/Button";
import IconHeader from "components/IconHeader";
import IconSubheader from "components/IconSubheader";
import withSessionToken from "components/withSessionToken";
import { payrollConstants } from "actions/types";
import { approveSessionTokenPayroll } from "actions/payrollActions";
import AuthorizationText from "components/AuthorizationText";

interface SessionParamsPayload {
  companyName: string;
  groupName: string;
  processingDate: string;
  accountNumber: string;
  payrollAmount: number | string;
  start: string;
  end: string;
}

interface SessionParams {
  sessionToken: string;
  identityHash: string;
  payload: SessionParamsPayload;
}

interface OwnProps {
  sessionParams: SessionParams;
}

interface State {
  showSuccessPage: boolean;
}

const actions = [payrollConstants.APPROVE_SESSION_TOKEN_PAYROLL];

const isLoadingSelector = createLoadingSelector(actions);
const isErrorSelector = createErrorSelector(actions);

const updatingSelector = createLoadingSelector(actions);

const mapStateToProps = (state: RootState) => {
  return {
    isFetching: isLoadingSelector(state),
    error: isErrorSelector(state),
    isUpdating: updatingSelector(state),
  };
};

const mapDispatchToProps = {
  push,
  approveSessionTokenPayroll,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = OwnProps & WithApolloClient<PropsFromRedux>;

class AuthlessPayrollApproval extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      showSuccessPage: false,
    };
  }

  _goToDashboard = () => {
    this.props.push("/dashboard");
  };

  _submitPayroll = () => {
    const { sessionToken, identityHash } = this.props.sessionParams;
    void this.props
      .approveSessionTokenPayroll(this.props.client as ApolloClient<object>, {
        sessionToken,
        identityHash,
      })
      .then((result) => {
        if (result.success) {
          toast.success("Successfully processed the payroll.");
          this.setState({ showSuccessPage: true });
        } else {
          // this route is session-token based, so stay put for a retry rather
          // than sending an unauthenticated employer to the private dashboard
          toast.error("There was a problem processing the payroll.");
        }
      });
  };

  render() {
    const {
      companyName,
      groupName,
      processingDate,
      accountNumber,
      payrollAmount,
      start,
      end,
    } = this.props.sessionParams.payload;

    const commonDateFormat = "MMM Do YYYY";

    const payPeriod = `Pay Period: ${moment(start).format(
      commonDateFormat
    )} -  ${moment(end).format(commonDateFormat)} `;

    if (this.state.showSuccessPage) {
      return (
        <>
          <div className="authlessPayrollApproval">
            <div className="authlessPayrollApproval-intro">
              <IconHeader
                variant="cardHeader"
                headerText="Successfully Approved Payroll"
              />
              <IconSubheader subheader={payPeriod} />
            </div>
            <div className="authlessPayrollApproval-body">
              <IconHeader variant="referralHeader" headerText="You did it!" />
              <IconSubheader
                subheader={`Time to relax, we will take it from here. Icon will debit ${formatCurrency(
                  payrollAmount
                )} from your account ending in ${accountNumber} on ${moment(
                  processingDate
                ).format(
                  commonDateFormat
                )}. Please make sure to have funds available.`}
              />

              <div className="authlessPayrollApproval">
                <Row>
                  <Col>
                    <div className="employerInfo">
                      <div className="employerInfo-body">
                        <div>
                          <div className="circle">
                            <span className="icon">
                              <FiGlobe
                                color="white"
                                stroke="#60A4BF"
                                size={16}
                              />
                            </span>
                          </div>
                        </div>
                        <div className="employerInfo-intro">
                          <IconHeader
                            variant="labelHeader"
                            headerText="Company"
                          />
                          <IconSubheader subheader={companyName} />
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col>
                    <div className="approval-box">
                      <div className="employerInfo">
                        <div className="employerInfo-body">
                          <div className="employerInfo-intro">
                            <IconHeader
                              variant="labelHeader"
                              headerText="Group"
                            />
                            <IconSubheader subheader={groupName} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col>
                    <div className="approval-box">
                      <div className="employerInfo">
                        <div className="employerInfo-body">
                          <div className="employerInfo-intro">
                            <IconHeader
                              variant="labelHeader"
                              headerText="Processing Date"
                            />
                            <IconSubheader
                              subheader={moment(processingDate).format(
                                commonDateFormat
                              )}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col>
                    <div className="approval-box">
                      <div className="employerInfo">
                        <div className="employerInfo-body">
                          <div className="employerInfo-intro">
                            <IconHeader
                              variant="labelHeader"
                              headerText="Total Contribution"
                            />
                            <IconSubheader
                              subheader={formatCurrency(payrollAmount)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="authlessPayrollApproval">
          <div className="authlessPayrollApproval-intro">
            <IconHeader variant="cardHeader" headerText="Process Payroll" />
            <IconSubheader subheader={payPeriod} />
          </div>
          <div className="authlessPayrollApproval-body">
            <IconHeader
              variant="referralHeader"
              headerText="Review and Submit"
            />
            <IconSubheader
              subheader={
                <AuthorizationText
                  bankAccountNumber={accountNumber}
                  amount={payrollAmount}
                  processingDate={moment(processingDate).format(
                    commonDateFormat
                  )}
                />
              }
            />

            <div>
              <Row>
                <Col sm={3}>
                  <div className="employerInfo">
                    <div className="employerInfo-body">
                      <div>
                        <div className="circle">
                          <span className="icon">
                            <FiGlobe color="white" stroke="#60A4BF" size={16} />
                          </span>
                        </div>
                      </div>
                      <div className="employerInfo-intro">
                        <IconHeader
                          variant="labelHeader"
                          headerText="Company"
                        />
                        <IconSubheader subheader={companyName} />
                      </div>
                    </div>
                  </div>
                </Col>
                <Col>
                  <div className="approval-box">
                    <div className="employerInfo">
                      <div className="employerInfo-body">
                        <div className="employerInfo-intro">
                          <IconHeader
                            variant="labelHeader"
                            headerText="Group"
                          />
                          <IconSubheader subheader={groupName} />
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
                <Col>
                  <div className="approval-box">
                    <div className="employerInfo">
                      <div className="employerInfo-body">
                        <div className="employerInfo-intro">
                          <IconHeader
                            variant="labelHeader"
                            headerText="Processing Date"
                          />
                          <IconSubheader
                            subheader={moment(processingDate).format(
                              commonDateFormat
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
                <Col>
                  <div className="approval-box">
                    <div className="employerInfo">
                      <div className="employerInfo-body">
                        <div className="employerInfo-intro">
                          <IconHeader
                            variant="labelHeader"
                            headerText="Bank Account"
                          />
                          <IconSubheader subheader={accountNumber} />
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
                <Col>
                  <div className="approval-box">
                    <div className="employerInfo">
                      <div className="employerInfo-body">
                        <div className="employerInfo-intro">
                          <IconHeader
                            variant="labelHeader"
                            headerText="Total Contribution"
                          />
                          <IconSubheader
                            subheader={formatCurrency(payrollAmount)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
            <div className="submit-row">
              <Button
                btnLabel="Approve Payroll"
                name="submit"
                withArrow={true}
                loading={this.props.isFetching}
                onClick={this._submitPayroll}
              />
            </div>
          </div>
        </div>
      </>
    );
  }
}

// withSessionToken is an untyped-boundary HOC: it accepts a loose
// `ComponentType<Record<string, unknown>>` and injects `sessionParams` while
// forwarding all other props via spread. Cast the class to that loose shape on
// the way in, and cast the wrapped result back to `ComponentType<Props>` so
// withApollo/connect can re-narrow the injected props. Behavior is unchanged.
const AuthlessPayrollApprovalWithSession = withSessionToken(
  AuthlessPayrollApproval as unknown as React.ComponentType<
    Record<string, unknown>
  >
) as React.ComponentType<Props>;

// sessionParams is injected by withSessionToken (from the getSessionParam
// query), client by withApollo, and the redux props by connect — so externally
// this route screen takes no props. withSessionToken's injection isn't reflected
// in the HOC-stack type, so cast the fully-wrapped export to a prop-less
// ComponentType. Behavior unchanged.
export default connector(
  withApollo<Props>(AuthlessPayrollApprovalWithSession)
) as unknown as React.ComponentType;
